import {
  Hotel, Video, Building2, Stethoscope, HeartPulse, ClipboardCheck,
  Baby, UserRound, HandHeart, Home as HomeIcon, Users, Syringe,
  MessagesSquare, Search, CalendarCheck, PhoneCall, ShieldCheck,
  Languages, Clock, BadgeCheck, Plane, MapPin as MapPinIcon,
  MessageCircle as MessageCircleIcon,
} from "lucide-react";

export const CONTACT = {
  brand: "Doctors Vietnam",
  brandDomain: "DoctorsVietnam.com",
  tagline: "English-speaking medical support in Vietnam",
  domain: "doctorsvietnam.com",
  legacyDomain: "vietnamclinics.com",
  whatsappDisplay: "+84 38 6492508",
  whatsappNumber: "84386492508",
  email: "support@vietclinics.com",
  positioning: "Doctors Vietnam is an independent healthcare coordination and patient-support service. We help travellers, expats and international patients find suitable medical support across Vietnam.",
  languages: ["English", "Vietnamese", "German", "French", "Russian"],
};

/**
 * Central, single source of truth for Doctors Vietnam social profiles.
 * Paste the OFFICIAL URLs here. Empty values are hidden everywhere automatically
 * (footer, structured data). WhatsApp uses the existing wa.me link — it's
 * kept in this map for footer rendering only and is intentionally not sent
 * to Organization.sameAs.
 */
export const socialLinks = {
  facebook: "",
  instagram: "",
  tiktok: "",
  youtube: "",
  linkedin: "",
  whatsapp: "", // leave empty to use the existing wa.me link automatically
  zalo: "",
};

export const waLink = (text) =>
  `https://wa.me/${CONTACT.whatsappNumber}?text=${encodeURIComponent(text || "Hello Doctors Vietnam, I need help finding a doctor in Vietnam.")}`;

export const mailLink = (subject) =>
  `mailto:${CONTACT.email}?subject=${encodeURIComponent(subject || "Doctors Vietnam support request")}`;

export const EMERGENCY_NOTICE =
  "For life-threatening emergencies in Vietnam, call 115 or go directly to the nearest hospital emergency department. Doctors Vietnam provides non-emergency medical coordination and does not diagnose or treat patients.";

export const INDEPENDENCE_POINTS = [
  "Doctors Vietnam is not a hospital.",
  "Doctors Vietnam is not a clinic.",
  "Doctors Vietnam is not an ambulance service.",
  "Doctors Vietnam is not an emergency department.",
  "Doctors Vietnam does not diagnose or provide medical treatment.",
  "Medical care is delivered by independent licensed doctors, clinics and hospitals.",
  "Service availability is confirmed individually.",
];

export const NAV = [
  { label: "Home", tKey: "home", to: "/" },
  {
    label: "Urgent Care",
    tKey: "urgentCare",
    to: "/urgent-care",
    children: [
      { label: "Doctor to Hotel", tKey: "doctorToHotel", to: "/urgent-care/doctor-to-hotel" },
      { label: "Online Doctor Consultation", tKey: "onlineConsultation", to: "/urgent-care/online-consultation" },
      { label: "Clinic & Hospital Booking", tKey: "clinicBooking", to: "/urgent-care/clinic-booking" },
      { label: "Specialist Referral", tKey: "specialistReferral", to: "/urgent-care/specialist-referral" },
      { label: "Post-Discharge Support", tKey: "postDischarge", to: "/urgent-care/post-discharge-support" },
    ],
  },
  {
    label: "Nursing & Home Care",
    tKey: "nursing",
    to: "/nursing",
    children: [
      { label: "Post-Operative Nursing", tKey: "postOperative", to: "/nursing/post-operative" },
      { label: "Elderly Care Support", tKey: "elderlyCare", to: "/nursing/elderly-care" },
      { label: "Mother & Baby Support", tKey: "motherBaby", to: "/nursing/mother-baby" },
      { label: "Short-Term Home Nursing", tKey: "shortTerm", to: "/nursing/short-term" },
    ],
  },
  { label: "Common Health Problems", tKey: "commonHealth", to: "/common-health-problems-vietnam" },
  { label: "Locations", tKey: "locations", to: "/locations" },
  { label: "Medical Tourism", tKey: "medicalTourism", to: "/medical-tourism" },
  { label: "How It Works", tKey: "howItWorks", to: "/how-it-works" },
  { label: "About", tKey: "about", to: "/about" },
  { label: "Contact", tKey: "contact", to: "/contact" },
];

export const URGENT_SERVICES = [
  {
    slug: "doctor-to-hotel",
    title: "Doctor to Hotel",
    icon: Hotel,
    short: "In-person doctor visits at your hotel or home.",
    intro:
      "We coordinate a licensed independent doctor to visit you at your hotel, apartment or home for non-emergency care — so you can be seen without travelling while unwell.",
    features: [
      "Non-emergency doctor visits to your hotel or accommodation",
      "English-speaking coordination from first contact",
      "Common travel illnesses, minor injuries and general concerns",
      "Prescriptions arranged through licensed providers where appropriate",
      "Clear guidance on next steps and follow-up",
    ],
    goodFor: ["Travellers feeling unwell", "Families with children", "Guests who prefer to stay in", "After-hours coordination"],
  },
  {
    slug: "online-consultation",
    title: "Online Doctor Consultation",
    icon: Video,
    short: "Speak with a doctor from anywhere.",
    intro:
      "Connect with a licensed independent doctor by video or phone for advice, minor concerns and guidance — a convenient first step when you are unsure what care you need.",
    features: [
      "Video or phone consultation with a licensed doctor",
      "Helpful for advice, minor issues and second opinions",
      "Coordination in English and other supported languages",
      "Guidance on whether an in-person visit is needed",
      "Digital summary and recommendations where available",
    ],
    goodFor: ["Quick medical advice", "Remote locations", "Follow-up questions", "Prescription guidance"],
  },
  {
    slug: "clinic-booking",
    title: "Clinic & Hospital Booking",
    icon: Building2,
    short: "We help arrange the right appointment for you.",
    intro:
      "We help you find and book a suitable clinic or hospital appointment with independent providers, matching your needs, location and language preferences.",
    features: [
      "Appointment coordination at suitable clinics and hospitals",
      "Matching by specialty, location and language",
      "Assistance understanding options and expected steps",
      "Support with scheduling and confirmation",
      "Coordination for international patients",
    ],
    goodFor: ["Planned appointments", "Tests and check-ups", "International patients", "Specialist visits"],
  },
  {
    slug: "specialist-referral",
    title: "Specialist Referral",
    icon: Stethoscope,
    short: "We connect you with the right medical specialists.",
    intro:
      "When a specific specialty is needed, we help connect you with suitable independent specialists and coordinate the referral so you can access the right care.",
    features: [
      "Guidance toward relevant independent specialists",
      "Coordination of referral information and appointments",
      "Support across a range of medical specialties",
      "Language-supported communication throughout",
      "Assistance with follow-up coordination",
    ],
    goodFor: ["Specialist opinions", "Complex needs", "Ongoing conditions", "Continuity of care"],
  },
  {
    slug: "post-discharge-support",
    title: "Post-Discharge Support",
    icon: ClipboardCheck,
    short: "Coordinated support after leaving hospital.",
    intro:
      "After a hospital stay or procedure, we help coordinate practical support — from follow-up appointments to nursing at your accommodation — for a smoother recovery.",
    features: [
      "Coordination of follow-up appointments and check-ins",
      "Arrangement of home or hotel nursing support",
      "Help understanding aftercare instructions",
      "Medication reminder and recovery coordination",
      "Ongoing communication with your coordinator",
    ],
    goodFor: ["After surgery", "After hospital stays", "Recovery abroad", "Peace of mind"],
  },
];

export const NURSING_SERVICES = [
  {
    slug: "post-operative",
    title: "Post-Operative Nursing",
    icon: Syringe,
    short: "Professional nursing support during recovery.",
    intro:
      "We coordinate qualified independent nurses to support your recovery after surgery — wound care coordination, medication reminders and daily wellbeing at your accommodation.",
    features: [
      "Recovery support after surgery or a procedure",
      "Coordination of wound care with licensed nurses",
      "Medication reminders and mobility support",
      "Regular check-ins and progress updates",
      "Care at your hotel, home or apartment",
    ],
    goodFor: ["After surgery", "Dental or cosmetic recovery", "Short-term recovery", "Travellers recovering abroad"],
  },
  {
    slug: "elderly-care",
    title: "Elderly Care Support",
    icon: UserRound,
    short: "Compassionate daily support for older adults.",
    intro:
      "We connect families with caring, qualified independent caregivers to support older adults with daily activities, companionship and wellbeing.",
    features: [
      "Support with daily activities and personal care",
      "Companionship and wellbeing check-ins",
      "Medication reminders and routine support",
      "Coordination with family members",
      "Flexible short-term or ongoing support",
    ],
    goodFor: ["Older travellers", "Family visits", "Daily assistance", "Companionship"],
  },
  {
    slug: "mother-baby",
    title: "Mother & Baby Support",
    icon: Baby,
    short: "Gentle support for new mothers and babies.",
    intro:
      "We help coordinate experienced independent nurses and caregivers to support new mothers and babies with practical, gentle care during the early days.",
    features: [
      "Support for new mothers and newborn care",
      "Practical guidance and reassurance at home",
      "Coordination of qualified maternity-experienced carers",
      "Flexible day or short-term support",
      "Caring, respectful and discreet",
    ],
    goodFor: ["New mothers", "Newborn support", "Postnatal recovery", "Extra reassurance"],
  },
  {
    slug: "short-term",
    title: "Short-Term Home Nursing",
    icon: HomeIcon,
    short: "Flexible nursing care when you need it.",
    intro:
      "For short-term needs, we coordinate qualified independent nurses to provide practical care at your accommodation — flexible, reliable and easy to arrange.",
    features: [
      "Short-term nursing at your hotel, home or apartment",
      "Flexible scheduling to fit your needs",
      "Practical care and wellbeing support",
      "English-speaking coordination",
      "Fast, clear arrangement",
    ],
    goodFor: ["Short recovery periods", "Temporary needs", "Travellers", "Peace of mind"],
  },
];

export const LOCATIONS = [
  { slug: "ho-chi-minh-city", name: "Ho Chi Minh City", region: "Southern Vietnam",
    blurb: "Vietnam's largest city and a major hub for international-standard clinics and hospitals.",
    areas: ["District 1", "District 3", "District 7 (Phu My Hung)", "Thao Dien"] },
  { slug: "hanoi", name: "Hanoi", region: "Northern Vietnam",
    blurb: "The capital city, home to established hospitals and English-speaking clinics.",
    areas: ["Hoan Kiem", "Tay Ho", "Ba Dinh", "Cau Giay"] },
  { slug: "da-nang", name: "Da Nang", region: "Central Vietnam",
    blurb: "A popular coastal city with growing healthcare options for visitors and residents.",
    areas: ["Hai Chau", "Son Tra", "My Khe Beach", "Ngu Hanh Son"] },
  { slug: "hoi-an", name: "Hoi An", region: "Central Vietnam",
    blurb: "A historic riverside town near Da Nang, popular with international visitors.",
    areas: ["Ancient Town", "An Bang Beach", "Cam Thanh", "Cua Dai"] },
  { slug: "nha-trang", name: "Nha Trang", region: "South-Central Coast",
    blurb: "A well-known beach destination where visitors often need travel-health support.",
    areas: ["City Centre", "Tran Phu Beach", "Vinh Nguyen", "Vinh Hoa"] },
  { slug: "vung-tau", name: "Vung Tau", region: "Southern Coast",
    blurb: "A coastal city near Ho Chi Minh City, popular with travellers and residents.",
    areas: ["Ward 1", "Back Beach", "Front Beach", "Ward 10"] },
  { slug: "phu-quoc", name: "Phu Quoc", region: "Island (Kien Giang)",
    blurb: "Vietnam's largest island and a growing resort destination for international visitors.",
    areas: ["Duong Dong", "An Thoi", "Long Beach", "Ong Lang"] },
];

export const STEPS = [
  { n: "01", title: "Tell Us What You Need", icon: MessagesSquare,
    text: "Share your location and what support you need by WhatsApp, phone or the form." },
  { n: "02", title: "We Review Your Request", icon: Search,
    text: "Our coordinator checks your needs and finds suitable independent options." },
  { n: "03", title: "We Arrange Suitable Care", icon: CalendarCheck,
    text: "We connect you with a licensed doctor, clinic or nurse quickly." },
  { n: "04", title: "Follow-Up Support", icon: HandHeart,
    text: "We stay in touch and help coordinate the next steps in your care." },
];

export const HERO_BULLETS = [
  "Doctor visits at your hotel or home",
  "Online consultations",
  "Clinic & hospital appointments",
  "Nursing & recovery support",
  "English-speaking coordination",
  "Trusted local providers",
];

export const TRUST_POINTS = [
  { icon: BadgeCheck, text: "Verified doctors, nurses and medical facilities" },
  { icon: Languages, text: "English-speaking patient coordinators" },
  { icon: Clock, text: "Fast, reliable and compassionate support" },
  { icon: MessagesSquare, text: "Clear communication and practical help" },
  { icon: HeartPulse, text: "Your health and comfort are our priority" },
];

export const HOME_SERVICE_ICONS = [
  { icon: Hotel, title: "Doctor to Hotel", to: "/urgent-care/doctor-to-hotel", text: "In-person doctor visits at your hotel or home." },
  { icon: Video, title: "Online Doctor Consultation", to: "/urgent-care/online-consultation", text: "Speak with a doctor from anywhere." },
  { icon: Building2, title: "Clinic & Hospital Booking", to: "/urgent-care/clinic-booking", text: "We help arrange the right appointment for you." },
  { icon: Stethoscope, title: "Specialist Referral", to: "/urgent-care/specialist-referral", text: "We connect you with the right specialists." },
  { icon: HeartPulse, title: "Nursing & Recovery", to: "/nursing", text: "Short-term nursing and practical care." },
  { icon: ClipboardCheck, title: "Post-Treatment Care", to: "/urgent-care/post-discharge-support", text: "Support after surgery or a hospital stay." },
];

export const REQUEST_TYPES = [
  { value: "medical", label: "Medical support" },
  { value: "nursing", label: "Nursing & home care" },
  { value: "general", label: "General enquiry" },
];

export const ICONS = { ShieldCheck, PhoneCall, Users };

// Homepage grid: 18 common concerns (do NOT include emergency symptoms here)
export const COMMON_CONCERNS = [
  "Food poisoning",
  "Diarrhoea",
  "Vomiting",
  "Fever",
  "Cold & flu symptoms",
  "Sore throat",
  "Cough / respiratory symptoms",
  "Stomach pain",
  "Dehydration",
  "Headaches & migraines",
  "Skin rashes",
  "Allergic reactions",
  "Ear pain / ear infections",
  "Urinary symptoms / possible UTI",
  "Minor cuts and wounds",
  "Sprains and minor injuries",
  "Heat exhaustion",
  "Insect bites",
  "Travel-related illness",
];

// Expanded services shown in the "How we can help" home section
export const HELP_SERVICES = [
  { icon: Stethoscope, title: "English-Speaking Doctors", to: "/urgent-care/specialist-referral",
    text: "We help patients connect with suitable English-speaking doctors across Vietnam." },
  { icon: Hotel, title: "Doctor to Hotel / Home", to: "/urgent-care/doctor-to-hotel",
    text: "Where available, we coordinate doctors who can visit your hotel, apartment or home." },
  { icon: Video, title: "Online Doctor Consultation", to: "/urgent-care/online-consultation",
    text: "We help arrange remote consultations with appropriate medical professionals." },
  { icon: Building2, title: "Clinic Appointments", to: "/urgent-care/clinic-booking",
    text: "Coordination of appointments with suitable private clinics." },
  { icon: HeartPulse, title: "Hospital Support", to: "/urgent-care/clinic-booking",
    text: "Help international patients identify and communicate with appropriate hospitals." },
  { icon: HandHeart, title: "Nursing & Home Care", to: "/nursing",
    text: "Nursing support at hotels, apartments or homes where available." },
  { icon: Syringe, title: "Post-Surgery Nursing", to: "/nursing/post-operative",
    text: "Short-term nursing and recovery support after medical or cosmetic procedures." },
  { icon: ClipboardCheck, title: "Medical Tests & Health Checks", to: "/urgent-care/clinic-booking",
    text: "Help coordinate blood tests, scans, diagnostic appointments and health screening." },
];

export const TRUST_STRIP = [
  { icon: Languages, text: "English-speaking coordination" },
  { icon: ShieldCheck, text: "Independent healthcare support" },
  { icon: Stethoscope, text: "Access to doctors, clinics and hospitals" },
  { icon: Plane, text: "Support for travellers and expats" },
  { icon: MapPinIcon, text: "Multiple locations across Vietnam" },
  { icon: MessageCircleIcon, text: "WhatsApp assistance" },
  { icon: HeartPulse, text: "Nursing and recovery support" },
];

export const MEDICAL_TOURISM_TREATMENTS = [
  "Dental treatment",
  "Cosmetic / plastic surgery",
  "Health checks",
  "Specialist consultations",
  "Hair restoration",
  "Eye care",
  "Other planned medical treatment",
];
